import { tr } from "./localization";
import { useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Moon,
  Send,
  UserRound,
  Check,
  Link as LinkIcon,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "./api";
import { err, Avatar, Button } from "./shared";

export function AuthPage({ onSuccess }: { onSuccess: () => Promise<void> }) {
  const { pathname } = useLocation(),
    [params] = useSearchParams(),
    navigate = useNavigate();
  const [email, setEmail] = useState(""),
    [password, setPassword] = useState(""),
    [repeat, setRepeat] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const token = params.get("token") || "";
  const invite = useQuery({
    queryKey: ["invitation", token],
    queryFn: () =>
      api<{
        email: string;
        name: string;
        inviter_name?: string;
        department_name?: string;
        role?: string;
      }>("/auth/invitation?token=" + encodeURIComponent(token)),
    enabled: pathname === "/invite" && !!token,
    retry: false,
  });
  const publicConfig = useQuery({
    queryKey: ["public-config"],
    queryFn: () => api<{ telegram_bot_url: string | null }>("/public-config"),
    enabled: pathname === "/forgot",
  });
  const isSet = pathname === "/invite" || pathname === "/reset";
  const [showPassword, setShowPassword] = useState(false);
  return (
    <div className="auth">
      <div className="auth-brand" aria-hidden="true">
        <img src="/IMS-logo.svg" alt="IMS" />
        <div className="auth-pitch">
          <h2>{tr("Поручения, сроки и приёмка — в одном месте")}</h2>
          <p>
            {tr(
              "Ставьте задачи коллегам, ведите их на досках и получайте уведомления в Telegram.",
            )}
          </p>
        </div>
        <div className="auth-mini">
          <div>
            <span className="avatar c1">АН</span>
            <span>
              <strong>{tr("Заказать подшипники SKF 6205")}</strong>
              <small>{tr("Срок: завтра · Срочно")}</small>
            </span>
          </div>
          <div>
            <span className="avatar c3">МЕ</span>
            <span>
              <strong>{tr("Свести заявки на квартал")}</strong>
              <small>{tr("Ждёт вашей приёмки")}</small>
            </span>
          </div>
        </div>
        <svg
          className="auth-rings"
          viewBox="0 0 100 100"
          fill="none"
          stroke="white"
          strokeWidth=".6"
        >
          <circle cx="50" cy="50" r="48" />
          <circle cx="50" cy="50" r="36" />
          <circle cx="50" cy="50" r="24" />
          <circle cx="50" cy="50" r="12" />
        </svg>
      </div>
      <div className="auth-form">
        <button
          className="btn icon auth-theme"
          aria-label={tr("Сменить тему")}
          onClick={() => {
            localStorage.setItem(
              "ims-theme",
              document.documentElement.dataset.theme === "dark"
                ? "light"
                : "dark",
            );
            window.dispatchEvent(new Event("ims-theme-changed"));
          }}
        >
          <Moon size={18} />
        </button>
        <img className="auth-mobile-logo" src="/IMS-logo.svg" alt="IMS" />
        <h1>
          {pathname === "/forgot"
            ? tr("Забыли пароль?")
            : pathname === "/invite"
              ? `Добро пожаловать${invite.data ? ", " + invite.data.name : ""}!`
              : pathname === "/reset"
                ? tr("Новый пароль")
                : tr("Вход в систему")}
        </h1>
        <p className="muted">
          {pathname === "/forgot"
            ? tr(
                "Ничего страшного — ссылку для нового пароля можно получить двумя способами",
              )
            : pathname === "/invite"
              ? invite.data?.inviter_name
                ? invite.data.inviter_name +
                  tr(
                    " пригласил(а) вас в IMS Tasks. Задайте пароль — и можно начинать.",
                  )
                : tr("Задайте пароль — и можно начинать.")
              : tr("Industrial Motion Solutions · внутренний сервис задач")}
        </p>
        {pathname === "/forgot" ? (
          <>
            <div className="auth-help-option">
              <span className="auth-help-icon">
                <Send size={22} />
              </span>
              <div>
                <b>{tr("Через Telegram-бота")}</b>
                <p className="muted">
                  {tr(
                    "Отправьте боту команду /reset — он пришлёт ссылку. Работает, если вы уже подключили Telegram.",
                  )}
                </p>
                {publicConfig.data?.telegram_bot_url ? (
                  <a
                    className="btn primary small"
                    href={publicConfig.data.telegram_bot_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Send size={14} />
                    {tr("Открыть бота")}
                  </a>
                ) : (
                  <small className="muted">
                    {tr("Администратор ещё не подключил Telegram-бота")}
                  </small>
                )}
              </div>
            </div>
            <div className="auth-help-option">
              <span className="auth-help-icon">
                <UserRound size={22} />
              </span>
              <div>
                <b>{tr("Попросите администратора")}</b>
                <p className="muted">
                  {tr(
                    "Он создаст новую ссылку за минуту. Напишите администратору или в общий чат.",
                  )}
                </p>
                <Button
                  className="small"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        tr(
                          "Здравствуйте! Прошу прислать ссылку для сброса пароля в IMS Tasks.",
                        ),
                      );
                      toast.success(tr("Сообщение скопировано"));
                    } catch (e) {
                      err(e);
                    }
                  }}
                >
                  <LinkIcon size={14} />
                  {tr("Скопировать сообщение")}
                </Button>
              </div>
            </div>
            <Link className="auth-back" to="/login">
              {tr("← Вернуться ко входу")}
            </Link>
          </>
        ) : (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setError("");
              if (isSet && password !== repeat) {
                setError(tr("Пароли не совпадают"));
                return;
              }
              setBusy(true);
              try {
                if (pathname === "/login")
                  await api("/auth/login", "POST", { email, password });
                else if (pathname === "/invite")
                  await api("/auth/accept-invitation", "POST", {
                    token,
                    password,
                  });
                else {
                  await api("/auth/reset-password", "POST", {
                    token,
                    password,
                  });
                  toast.success(tr("Пароль изменён. Войдите с новым паролем"));
                  location.href = "/login";
                  return;
                }
                await onSuccess();
                if (pathname === "/invite")
                  navigate("/first-run", { replace: true });
              } catch (e) {
                setError(
                  e instanceof Error ? e.message : tr("Не удалось войти"),
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            {!isSet && (
              <label>
                {tr("Рабочая почта")}
                <input
                  autoComplete="username"
                  placeholder="name@industrialmotion.kz"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
            )}
            {invite.data && (
              <div className="surface invite-person">
                <Avatar
                  user={{
                    id: invite.data.email,
                    name: invite.data.name,
                    department_id: null,
                    position: "",
                  }}
                />
                <div>
                  <b>{invite.data.name}</b>
                  <small className="muted">
                    {[
                      invite.data.email,
                      invite.data.department_name,
                      invite.data.role === "manager"
                        ? tr("Руководитель")
                        : invite.data.role === "admin"
                          ? tr("Администратор")
                          : tr("Сотрудник"),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </small>
                </div>
              </div>
            )}
            {invite.error && (
              <p className="form-error">
                {tr(
                  "Ссылка уже использована или истекла. Попросите новое приглашение.",
                )}
              </p>
            )}
            <div className="password-field">
              <span className="password-label">
                <label htmlFor="auth-password">
                  {isSet ? tr("Придумайте пароль") : tr("Пароль")}
                </label>
                {!isSet && <Link to="/forgot">{tr("Забыли пароль?")}</Link>}
              </span>
              <span className="password-input">
                <input
                  id="auth-password"
                  autoComplete={isSet ? "new-password" : "current-password"}
                  type={showPassword ? "text" : "password"}
                  placeholder={
                    isSet ? tr("Не короче 8 символов") : tr("Введите пароль")
                  }
                  minLength={isSet ? 8 : undefined}
                  maxLength={256}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="btn icon"
                  aria-label={
                    showPassword ? tr("Скрыть пароль") : tr("Показать пароль")
                  }
                  onClick={() => setShowPassword((v) => !v)}
                >
                  <Eye size={16} />
                </button>
              </span>
            </div>
            {isSet && (
              <div className="password-meter" aria-hidden="true">
                {[1, 2, 3, 4].map((i) => (
                  <i
                    key={i}
                    className={
                      i <=
                      (password.length >= 8
                        ? /[a-zа-я]/i.test(password) && /\d/.test(password)
                          ? password.length >= 12
                            ? 4
                            : 3
                          : 2
                        : password.length
                          ? 1
                          : 0)
                        ? "active"
                        : ""
                    }
                  />
                ))}
              </div>
            )}
            {isSet && (
              <ul className="password-rules">
                <li className={password.length >= 8 ? "ok" : ""}>
                  <Check size={14} />
                  {tr("Не короче 8 символов")}
                </li>
                <li
                  className={
                    /[a-zа-я]/i.test(password) && /\d/.test(password)
                      ? "ok"
                      : ""
                  }
                >
                  <Check size={14} />
                  {tr("Есть буквы и цифры")}
                </li>
              </ul>
            )}
            {isSet && (
              <label>
                {tr("Повторите пароль")}
                <input
                  autoComplete="new-password"
                  type="password"
                  value={repeat}
                  onChange={(e) => setRepeat(e.target.value)}
                  required
                />
              </label>
            )}
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              className="primary full"
              disabled={busy || !!invite.error}
            >
              {busy
                ? tr("Подождите…")
                : pathname === "/invite"
                  ? tr("Создать аккаунт и войти")
                  : isSet
                    ? tr("Сохранить пароль")
                    : tr("Войти")}
            </Button>
            <p className="muted">
              {pathname === "/invite"
                ? tr("Ссылка действует 7 дней и работает один раз")
                : pathname === "/reset"
                  ? tr("Ссылка работает один раз")
                  : tr(
                      "Нет доступа? Попросите администратора прислать ссылку-приглашение.",
                    )}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
